import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowLeftRight,
  ChevronDown,
  ChevronUp,
  Plus,
  SlidersHorizontal,
  X,
} from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import FilterSelect from "src/components/ui/FilterSelect";
import Card from "src/components/common/card/Card";
import PageSizeSelect from "src/components/ui/PageSizeSelect";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as inventoryActions from "src/redux/slices/inventorySlice";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import useAssignedWarehouse from "src/hooks/useAssignedWarehouse";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import productApis from "src/api/product";
import { formatDateTime } from "src/utils/date-time";
import { formatVariantLabel } from "src/utils/common";
import { Product } from "src/types/product.types";

const TYPE_OPTIONS = [
  { label: "Opening", value: "opening" },
  { label: "Adjustment", value: "adjustment" },
  { label: "Purchase", value: "purchase" },
  { label: "Sale", value: "sale" },
  { label: "Transfer", value: "transfer" },
];

const TYPE_BADGE_COLOR: Record<string, "green" | "red" | "blue" | "gray" | "gold"> = {
  opening: "blue",
  adjustment: "gold",
  purchase: "green",
  sale: "red",
  transfer: "blue",
};

const StockMovementsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [warehouseId, setWarehouseId] = useState("");
  const [referenceType, setReferenceType] = useState("");
  const [productId, setProductId] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [limit, setLimit] = useState(10);

  const { transactions } = useAppSelector((state) => state.inventory);
  const { warehouseList } = useAppSelector((state) => state.warehouse);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canAdjust =
    isTenantAdmin || (permissions?.stockAdjustment || []).includes("create");
  const { assignedWarehouseId, isWarehouseLocked } = useAssignedWarehouse();
  const { singular: warehouseLabel, plural: warehouseLabelPlural } = useWarehouseLabel();

  const rows = transactions?.data?.data || [];

  useEffect(() => {
    if (isWarehouseLocked && assignedWarehouseId) setWarehouseId(assignedWarehouseId);
  }, [isWarehouseLocked, assignedWarehouseId]);

  useEffect(() => {
    dispatch(warehouseActions.getWarehouseListRequest({ page: 1, limit: 100 }));
    productApis
      .getProductList({ page: 1, limit: 100, status: "active" })
      .then((res: any) => setProducts(res?.data?.data || res?.data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, referenceType, productId, limit]);

  const getList = (page: number) =>
    dispatch(
      inventoryActions.getTransactionListRequest({
        page,
        limit,
        ...(warehouseId ? { warehouseId } : {}),
        ...(referenceType ? { referenceType } : {}),
        ...(productId ? { productId } : {}),
      })
    );

  const handlePageChange = (page: number) => getList(page);

  const handleClearFilters = () => {
    setWarehouseId("");
    setReferenceType("");
    setProductId("");
  };
  const hasActiveFilters = !!(warehouseId || referenceType || productId);

  const warehouseOptions = (warehouseList?.data?.data || []).map((w: any) => ({
    label: w.name,
    value: w._id,
  }));
  const productOptions = products.map((p) => ({
    label: p.name || "",
    value: p._id || "",
  }));

  return (
    <DashboardLayout
      meta={{
        title: "Stock Movements | Invyntra",
        description: "History of stock in and out.",
        keywords: "stock movements, inventory, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <ArrowLeftRight size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Stock movements
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                History of stock in and out.
              </p>
            </div>
          </div>

          {canAdjust && (
            <Button rounded="full" onClick={() => navigate("/app/stock/adjust")}>
              <Plus size={16} className="mr-1" />
              Adjust stock
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <FilterSelect
                variant="boxed"
                label={warehouseLabel}
                value={warehouseId}
                onChange={setWarehouseId}
                allLabel={`All ${warehouseLabelPlural.toLowerCase()}`}
                options={warehouseOptions}
                disabled={isWarehouseLocked}
                helperText={
                  isWarehouseLocked
                    ? `Locked to your assigned ${warehouseLabel.toLowerCase()}.`
                    : undefined
                }
              />
              <FilterSelect
                variant="boxed"
                label="Type"
                value={referenceType}
                onChange={setReferenceType}
                allLabel="All types"
                options={TYPE_OPTIONS}
              />
              <FilterSelect
                variant="boxed"
                label="Product"
                value={productId}
                onChange={setProductId}
                allLabel="All products"
                options={productOptions}
              />
              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* List */}
        <Card>
          <CardHeader
            title="Stock movements"
            description="History of stock in and out."
            actions={<PageSizeSelect value={limit} onChange={setLimit} />}
          />

          <TableV2
            columns={[
              {
                key: "createdAt",
                header: "Date",
                accessor: ({ row }) => (
                  <span className="whitespace-nowrap text-sm">
                    {formatDateTime(row.createdAt, "DD MMM YYYY, hh:mm A")}
                  </span>
                ),
              },
              {
                key: "product",
                header: "Product",
                width: "220px",
                accessor: ({ row }) => (
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {row.productId?.name || "-"}
                  </span>
                ),
              },
              {
                key: "variant",
                header: "Variant",
                accessor: ({ row }) => {
                  const variant = (row.productId?.variants || []).find(
                    (v: any) => String(v._id) === String(row.variantId)
                  );
                  return variant ? (
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {formatVariantLabel(variant.optionValues)}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">-</span>
                  );
                },
              },
              {
                key: "warehouse",
                header: warehouseLabel,
                accessor: ({ row }) => row.warehouseId?.name || "-",
              },
              {
                key: "referenceType",
                header: "Type",
                accessor: ({ row }) => (
                  <Badge
                    text={row.referenceType}
                    color={TYPE_BADGE_COLOR[row.referenceType] || "gray"}
                  />
                ),
              },
              {
                key: "quantity",
                header: "Quantity",
                accessor: ({ row }) => (
                  <span
                    className={`font-medium ${
                      row.direction === "in"
                        ? "text-green-600 dark:text-green-400"
                        : "text-red-600 dark:text-red-400"
                    }`}
                  >
                    {row.direction === "in" ? "+" : "-"}
                    {row.quantity}
                  </span>
                ),
              },
              {
                key: "referenceId",
                header: "Reference",
                accessor: ({ row }) => row.referenceId || "-",
              },
            ]}
            data={rows}
            pagination={transactions?.data?.pagination}
            onPageChange={handlePageChange}
            loading={transactions.loading}
          />
        </Card>

        <button
          onClick={() => navigate("/app/stock/levels")}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeft size={14} />
          Back to Inventory
        </button>
      </div>
    </DashboardLayout>
  );
};

export default StockMovementsPage;
